import { useCallback, useState, useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import {
    DESKTOP_ASPECT,
    getGeometrySize,
    MAX_SCREEN_SIZE,
    MOBILE_ASPECT,
    MOBILE_THRESHOLD,
    SIZE_FACTOR,
} from '@/lib/geometry';

interface UseResponsiveScalar {
    responsiveValue: (desktopVal: any, mobileVal?: any) => any;
    isMobileSize: boolean;
    scale: [number, number, number];
    contentBounds: {
        left: number;
        right: number;
        bottom: number;
        top: number;
    };
}

export const useResponsive = (): UseResponsiveScalar => {
    const { viewport, size } = useThree();
    const [containerDimensions, setContainerDimensions] = useState<[number, number]>([0, 0]);
    const { width, height, factor } = viewport.getCurrentViewport();
    const isMobileSize = size.width < MOBILE_THRESHOLD;

    // Returns one of 2 numbers depending on if the canvas width exceeds the MOBILE_THRESHOLD
    const responsiveValue = useCallback(
        (desktopVal: number, mobileVal: number): number => (isMobileSize ? mobileVal : desktopVal),
        [isMobileSize]
    );

    useEffect(() => {
        let containerWidth = width;
        let containerHeight = height;

        const maxWidth = MAX_SCREEN_SIZE[0] / factor;
        const maxHeight = MAX_SCREEN_SIZE[1] / factor;
        if (containerWidth > maxWidth) {
            containerWidth = maxWidth;
        }
        if (containerHeight > maxHeight) {
            containerHeight = maxHeight;
        }
        setContainerDimensions([containerWidth, containerHeight]);
    }, [width, height, factor, setContainerDimensions]);

    // calculate width, height, and margins given container, ratio, and size factor
    const [w, h] = getGeometrySize(
        containerDimensions[0],
        containerDimensions[1],
        responsiveValue(DESKTOP_ASPECT, MOBILE_ASPECT),
        SIZE_FACTOR
    );

    // calculate the bounds of typical content (eg. screen)
    const left = -w / 2;
    const right = left + w;
    const top = h / 2;
    const bottom = top - h;
    const contentBounds = {
        left,
        right,
        bottom,
        top,
    };

    const widthScalar = responsiveValue(8.66, 4); // found empirically
    const scale: [number, number, number] = [w / widthScalar, w / widthScalar, 1];

    return {
        responsiveValue,
        isMobileSize,
        scale,
        contentBounds,
    };
};
